import { IoSearchOutline } from 'react-icons/io5';
import { FiDownload } from 'react-icons/fi';
import React, { useState, useEffect, useContext } from 'react';
import Button from '../../components/shared/custom-btn';
import Heading from '../../components/shared/heading';
import DateRangePicker from '../../components/shared/date-range-picker';
import Modal from '../../components/shared/modal';
import IncidentTable from './incident-table';
import AddModal from './add-modal';
import { AccountsContext } from '../../components/context/account-context';
import { getAllIncidents, getAllIncidentStatuses, getAllIncidentTypes } from '../../services/incidents-service';
import { getUsersMinimal } from '../../services/users-service';
import { getVehiclesMinimal } from '../../services/vehicles-service';
import { CSVLink } from 'react-csv';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import logo from '../../assets/images/logo.jpg';
import { getDateRangeUTC, formatUTCTimestampToLocal } from '../../utils/set-date-range-utils';
import { format } from 'date-fns';
import Help from './help';
import { usePageHelp } from '../../components/context/help-context';

const Incidents = () => {
    usePageHelp(Help);
    const { accountId } = useContext(AccountsContext);
    const [searchTerm, setSearchTerm] = useState('');
    const [openAddModal, setOpenAddModal] = useState(false);
    const [dateRange, setDateRange] = useState({
        startDate: new Date(new Date().setDate(new Date().getDate() - 7)),
        endDate: new Date(),
    });
    const [newlyAddedItem, setNewlyAddedItem] = useState(null);
    const [incidentData, setIncidentData] = useState([]);
    const [allIncidentData, setAllIncidentData] = useState([]);
    const [error, setError] = useState(null);
    const [drivers, setDrivers] = useState([]);
    const [vehicles, setVehicles] = useState([]);
    const [statusOptions, setStatusOptions] = useState([]);
    const [typesOptions, setTypesOptions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [globalDataloading, setGlobalDataLoading] = useState(false);
    const [selectedRows, setSelectedRows] = useState({});

    const fetchGlobalData = async () => {

        if (!accountId) return;
        setGlobalDataLoading(true);

        try {
            const [statusOptionsRes, typesRes] = await Promise.all([
                getAllIncidentStatuses(accountId),
                getAllIncidentTypes(accountId),
            ]);

            if (statusOptionsRes.success) setStatusOptions(statusOptionsRes.data.data || []);
            if (typesRes.success) setTypesOptions(typesRes.data.data || []);
        } catch (err) {
            console.error("Failed to fetch global data", err);
            setError('Failed to fetch global data');
        } finally {
            setGlobalDataLoading(false);
        }
    };

    const fetchGlobalData2 = async () => {
        if (!accountId) return;
        setGlobalDataLoading(true);

        try {
            const [usersRes, vehiclesRes] = await Promise.all([
                getUsersMinimal(accountId),
                getVehiclesMinimal(accountId),
            ]);

            if (usersRes.success) setDrivers(usersRes.data.data || []);
            if (vehiclesRes.success) setVehicles(vehiclesRes.data.data || []);
        } catch (err) {
            console.error('Failed to fetch global data:', err);
            setError('Failed to fetch global data');
        } finally {
            setGlobalDataLoading(false);
        }
    };

    const fetchMainGlobalData = () => {
        fetchGlobalData();
        fetchGlobalData2();
    }

    const fetchIncidents = async () => {
        setLoading(true);
        try {
            const { from, to } = getDateRangeUTC(dateRange);
            console.log('Fetching incidents from:', from, 'to:', to);

            const response = await getAllIncidents(accountId, from, to);
            console.log('Incidents response:', response);

            if (response.success) {
                const sortedData = response.data.data.sort((a, b) => new Date(b.createdOn) - new Date(a.createdOn)); // sort latest first
                setAllIncidentData(sortedData || []);
                setIncidentData(sortedData || []);
                setError(null);
            } else {
                setError(response.data.message || 'Failed to fetch incident records');
            }
        } catch (err) {
            console.error('Error fetching incidents:', err);
            setError('An error occurred while fetching incident records');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchIncidents();
    }, [accountId, dateRange]);

    const handleOpenAddModal = () => {
        setOpenAddModal(true);
    };

    const handleCloseAddModal = () => {
        setOpenAddModal(false);
    };

    const handleAddItem = (newItem) => {
        setNewlyAddedItem(newItem);
        fetchIncidents(); // Refresh data after adding
    };

    const handleOpenEditModal = (item) => {
        setNewlyAddedItem(item);
    };

    const handleClearNewlyAddedItem = () => {
        setNewlyAddedItem(null);
    };

    // Filter data for export based on selected rows
    const exportData = Object.keys(selectedRows).length > 0
        ? allIncidentData.filter((_, index) => selectedRows[index])
        : allIncidentData;

    const csvData = exportData.map(item => ({
        IncidentID: item.incidentId,
        IncidentTime: formatUTCTimestampToLocal(item.incidentTime, 'yyyy-MM-dd HH:mm'),
        Vehicle: item.vehicle?.name || '',
        Type: item.type,
        Status: item.status,
        Location: item.location,
        Note: item.note,
        Causer: item.causer?.name || '',
        Creator: item.creator?.name || '',
        CreatedOn: formatUTCTimestampToLocal(item.createdOn, 'yyyy-MM-dd HH:mm'),
        AccountID: item.accountId,
    }));

    const handleExportPDF = () => {
        const doc = new jsPDF();

        // Logo placement
        const logoWidth = 25;
        const logoHeight = 15;
        const pageWidth = doc.internal.pageSize.getWidth();
        const logoX = (pageWidth - logoWidth) / 2;
        const logoY = 2;

        doc.addImage(logo, 'PNG', logoX, logoY, logoWidth, logoHeight);

        // Title placement
        const title = 'Incident Report';
        const titleY = logoY + logoHeight + 5;
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        const titleWidth = doc.getTextWidth(title);
        const titleX = (pageWidth - titleWidth) / 2;
        doc.text(title, titleX, titleY);

        // Date range placement
        const start = format(dateRange.startDate, 'yyyy-MM-dd');
        const end = format(dateRange.endDate, 'yyyy-MM-dd');
        const dateRangeText = `Date Range: ${start} to ${end}`;
        const dateRangeY = titleY + 5;
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        const dateRangeWidth = doc.getTextWidth(dateRangeText);
        const dateRangeX = (pageWidth - dateRangeWidth) / 2;
        doc.text(dateRangeText, dateRangeX, dateRangeY);

        // Table
        autoTable(doc, {
            head: [
                ['Incident ID', 'Incident Time', 'Vehicle', 'Type', 'Status', 'Location', 'Note', 'Causer',],
            ],
            body: exportData.map(item => [
                item.incidentId,
                formatUTCTimestampToLocal(item.incidentTime, 'yyyy-MM-dd HH:mm'),
                item.vehicle?.name || '',
                item.type,
                item.status,
                item.location,
                item.note,
                item.causer?.name || '',
            ]),
            startY: dateRangeY + 5, // Start table below date range with 5mm spacing
        });

        // Format filename with date range
        const fileName = `incidents-${start}-to-${end}.pdf`;
        doc.save(fileName);
    };

    const isExportDisabled = Object.keys(selectedRows).length === 0;

    return (
        <div className="px-6 py-4">
            <div className="flex mb-2 items-center justify-between">
                <div>
                    <Heading>Incidents</Heading>
                </div>
                <Button
                    label="Add Incident"
                    onClick={handleOpenAddModal}
                    className="text-white px-4 !py-1 rounded-lg bg-btnBg"
                />
            </div>

            <div className="space-y-4">
                {error && (
                    <div className="text-red-600 bg-red-100 px-4 py-2 rounded-md">
                        {error}
                    </div>
                )}

                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex gap-3 flex-wrap">
                        <div className="bg-white rounded-md px-3 flex border border-border items-center gap-2 w-44 lg:w-72 py-1">
                            <IoSearchOutline className="text-gray-500 text-lg" />
                            <input
                                type="text"
                                className="bg-transparent outline-none py-1 w-full text-sm text-gray-700"
                                placeholder="Search"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>

                        <DateRangePicker
                            onChange={setDateRange}
                            initialRange={{
                                startDate: dateRange.startDate,
                                endDate: dateRange.endDate,
                            }}
                        />
                    </div>

                    <div className="flex gap-2 items-center">
                        <Button
                            onClick={handleExportPDF}
                            className={` ${isExportDisabled ? '!cursor-not-allowed !opacity-75' : 'hover:!bg-gray-50'} flex items-center gap-1 !text-sm !px-3 !py-1.5 border rounded-md `}
                            label="PDF"
                            disabled={isExportDisabled}
                            icon={<FiDownload className="text-gray-600" />}
                            iconPosition="left"
                        />
                        <div
                            className={`flex items-center gap-1 !text-sm !px-3 !py-1.5 border rounded-md ${isExportDisabled ? 'cursor-not-allowed opacity-75' : 'hover:bg-gray-50'}`}
                        >
                            <CSVLink
                                data={csvData}
                                filename={`incidents-${format(dateRange.startDate, 'yyyy-MM-dd')}-to-${format(dateRange.endDate, 'yyyy-MM-dd')}.csv`}
                                className={`${isExportDisabled ? 'cursor-not-allowed' : ''} flex items-center gap-1`}
                                onClick={(event) => {
                                    if (isExportDisabled) {
                                        event.preventDefault();
                                    }
                                }}
                            >
                                <FiDownload className="text-gray-600" />
                                CSV
                            </CSVLink>
                        </div>
                    </div>
                </div>

                <IncidentTable
                    data={incidentData}
                    setData={setIncidentData}
                    globalFilter={searchTerm}
                    onGlobalFilterChange={setSearchTerm}
                    newlyAddedItem={newlyAddedItem}
                    setNewlyAddedItem={setNewlyAddedItem}
                    onClearNewlyAddedItem={handleClearNewlyAddedItem}
                    fetchIncidents={fetchIncidents}
                    loadingData={loading}
                    accountId={accountId}
                    drivers={drivers}
                    vehicles={vehicles}
                    statusOptions={statusOptions}
                    typesOptions={typesOptions}
                    globalDataloading={globalDataloading}
                    fetchMainGlobalData={fetchMainGlobalData}
                    setSelectedRows={setSelectedRows}
                    selectedRows={selectedRows}
                />

                <Modal
                    isOpen={openAddModal}
                    onClose={handleCloseAddModal}
                    modalClass="xl:w-[40%] lg:w-[45%] md:w-[65%] sm:w-[70%] w-[90%] !px-0"
                >
                    <AddModal
                        handleCloseAddModal={handleCloseAddModal}
                        onAdd={handleAddItem}
                        openEditModal={handleOpenEditModal}
                        drivers={drivers}
                        vehicles={vehicles}
                        accountId={accountId}
                        typesOptions={typesOptions}
                        globalDataloading={globalDataloading}
                        fetchMainGlobalData={fetchMainGlobalData}
                    />
                </Modal>
            </div>
        </div>
    );
};

export default Incidents;